import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, Coins, KeyRound, PauseCircle, PlayCircle } from 'lucide-react';
import { ClientRow, DocumentRow, LedgerEntry, SeriesPoint, TariffTier, errorMessage, operatorApi, priceForQuantity, useFetch } from '../../services/operatorApi';
import {
  Badge, Button, Card, DocumentStatusBadge, Empty, ErrorBox, Field, Modal, PageHeader, SecretBox, Spinner, StatCard, Table, Tabs, Td, ago, dateTime, eur, inputClass, num,
} from '../../components/ui';

interface KeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface Detail {
  client: ClientRow;
  keys: KeyRow[];
  ledger: LedgerEntry[];
  documents: Omit<DocumentRow, 'client' | 'client_id' | 'sent_at' | 'expires_at' | 'signature_style'>[];
  series: SeriesPoint[];
  active_webhooks: number;
  purchases_cents: number;
}

const KIND_LABELS: Record<string, [string, 'green' | 'blue' | 'slate' | 'amber']> = {
  purchase: ['Achat de lot', 'green'],
  grant: ['Offert', 'blue'],
  consumption: ['Signature', 'slate'],
  adjustment: ['Correction', 'amber'],
};

type TabId = 'credits' | 'keys' | 'documents' | 'info';

export default function ClientDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<TabId>('credits');
  const [modal, setModal] = useState<'credits' | 'key' | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data, error, loading, reload } = useFetch(async () => (await operatorApi.get<Detail>(`/clients/${id}`)).data, [id]);

  if (loading && !data) return <Spinner />;
  if (error && !data) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;
  const { client } = data;

  const toggleActive = async () => {
    if (client.is_active && !window.confirm(`Suspendre ${client.name} ? Ses clés API cesseront immédiatement de fonctionner.`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await operatorApi.patch(`/clients/${client.id}`, { is_active: !client.is_active });
      await reload();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/clients" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" />
        Clients
      </Link>
      <PageHeader
        title={client.name}
        subtitle={`${client.email} · client depuis le ${dateTime(client.created_at).slice(0, 10)}`}
        actions={
          <>
            {client.is_active ? <Badge tone="green">Actif</Badge> : <Badge tone="red">Suspendu</Badge>}
            <Badge tone={client.billing_mode === 'prepaid' ? 'violet' : 'slate'}>{client.billing_mode === 'prepaid' ? 'Lots prépayés' : 'Accès libre'}</Badge>
            <Button variant="secondary" onClick={toggleActive} loading={busy}>
              {client.is_active ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
              {client.is_active ? 'Suspendre' : 'Réactiver'}
            </Button>
            <Button onClick={() => setModal('credits')}>
              <Coins className="w-4 h-4" />
              Créditer
            </Button>
          </>
        }
      />
      {actionError && <div className="mb-4"><ErrorBox message={actionError} /></div>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Solde disponible"
          value={client.billing_mode === 'free' ? '—' : num(client.available)}
          tone={client.billing_mode === 'free' ? 'default' : client.balance < 0 ? 'bad' : client.available <= client.low_credit_threshold ? 'warn' : 'good'}
          hint={client.billing_mode === 'free' ? 'accès libre, aucun contrôle' : `${num(client.balance)} au solde − ${num(client.committed)} déjà attendues`}
        />
        <StatCard label="Signatures" value={num(client.signatures_total)} hint={`${num(client.signatures_30d)} sur 30 jours`} />
        <StatCard label="Documents" value={num(client.documents_total)} hint={`dernier ${ago(client.last_document_at)}`} />
        <StatCard label="Achats cumulés" value={eur(data.purchases_cents)} hint={`${data.active_webhooks} webhook(s) actif(s)`} />
      </div>

      <Card title="Signatures par jour (30 jours)" className="mt-6">
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" tickFormatter={(v: string) => `${v.slice(8, 10)}/${v.slice(5, 7)}`} tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip />
              <Area type="monotone" dataKey="signatures" name="Signatures" stroke="#4f46e5" strokeWidth={2} fill="#4f46e5" fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="mt-6">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: 'credits', label: 'Crédits' },
            { id: 'keys', label: `Clés API (${data.keys.filter((k) => !k.revoked_at).length})` },
            { id: 'documents', label: 'Documents' },
            { id: 'info', label: 'Informations' },
          ]}
        />

        {tab === 'credits' && (
          <Card>
            {data.ledger.length === 0 ? (
              <Empty>Aucun mouvement.</Empty>
            ) : (
              <div className="max-h-[28rem] overflow-y-auto">
              <Table head={['Date', 'Type', 'Crédits', 'Montant', 'Détail']}>
                {data.ledger.map((entry) => (
                  <tr key={entry.id}>
                    <Td className="whitespace-nowrap">{dateTime(entry.created_at)}</Td>
                    <Td><Badge tone={KIND_LABELS[entry.kind][1]}>{KIND_LABELS[entry.kind][0]}</Badge></Td>
                    <Td className={`tabular-nums font-medium ${entry.credits < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{entry.credits > 0 ? `+${entry.credits}` : entry.credits}</Td>
                    <Td className="tabular-nums">{entry.amount_cents === null ? '—' : eur(entry.amount_cents)}</Td>
                    <Td className="text-slate-500">
                      {entry.document_id ? <Link className="hover:text-indigo-600" to={`/documents/${entry.document_id}`}>{entry.document_title}</Link> : [entry.reference, entry.note].filter(Boolean).join(' · ') || '—'}
                    </Td>
                  </tr>
                ))}
              </Table>
              </div>
            )}
          </Card>
        )}

        {tab === 'keys' && (
          <Card
            action={
              <Button small onClick={() => setModal('key')}>
                <KeyRound className="w-4 h-4" />
                Nouvelle clé
              </Button>
            }
            title="Clés API"
          >
            <KeysTable keys={data.keys} clientId={client.id} onChanged={reload} />
          </Card>
        )}

        {tab === 'documents' && (
          <Card
            action={<Link className="text-xs font-medium text-indigo-600 hover:underline" to={`/documents?client=${client.id}`}>Tout voir</Link>}
            title="Derniers documents"
          >
            {data.documents.length === 0 ? (
              <Empty>Aucun document.</Empty>
            ) : (
              <Table head={['Document', 'Statut', 'Signatures', 'Créé']}>
                {data.documents.map((d) => (
                  <tr key={d.id}>
                    <Td><Link className="font-medium text-slate-900 hover:text-indigo-600" to={`/documents/${d.id}`}>{d.title}</Link></Td>
                    <Td><DocumentStatusBadge status={d.status} /></Td>
                    <Td className="tabular-nums">{d.signers_signed} / {d.signers_total}</Td>
                    <Td className="text-slate-500">{ago(d.created_at)}</Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        )}

        {tab === 'info' && <InfoForm client={client} onSaved={reload} />}
      </div>

      {modal === 'credits' && <CreditModal client={client} onClose={() => setModal(null)} onDone={reload} />}
      {modal === 'key' && <KeyModal clientId={client.id} onClose={() => setModal(null)} onDone={reload} />}
    </div>
  );
}

function KeysTable({ keys, clientId, onChanged }: { keys: KeyRow[]; clientId: string; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);

  const revoke = async (key: KeyRow) => {
    if (!window.confirm(`Révoquer la clé ${key.prefix}… ? Les applications qui l'utilisent cesseront de fonctionner.`)) return;
    try {
      await operatorApi.post(`/clients/${clientId}/keys/${key.id}/revoke`);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  if (keys.length === 0) return <Empty>Aucune clé.</Empty>;
  return (
    <>
      {error && <div className="mb-3"><ErrorBox message={error} /></div>}
      <Table head={['Clé', 'Droits', 'Dernière utilisation', 'Expiration', 'État', '']}>
        {keys.map((key) => {
          const expired = key.expires_at && new Date(`${key.expires_at}Z`) < new Date();
          return (
            <tr key={key.id}>
              <Td>
                <div className="font-medium text-slate-900">{key.name}</div>
                <code className="text-xs text-slate-500">{key.prefix}…</code>
              </Td>
              <Td>{key.scopes === 'documents:read' ? <Badge>Lecture seule</Badge> : <Badge tone="blue">Complets</Badge>}</Td>
              <Td className="text-slate-500">{ago(key.last_used_at)}</Td>
              <Td className="text-slate-500">{key.expires_at ? dateTime(key.expires_at) : 'jamais'}</Td>
              <Td>{key.revoked_at ? <Badge tone="red">Révoquée</Badge> : expired ? <Badge tone="amber">Expirée</Badge> : <Badge tone="green">Active</Badge>}</Td>
              <Td className="text-right">{!key.revoked_at && <Button variant="danger" small onClick={() => revoke(key)}>Révoquer</Button>}</Td>
            </tr>
          );
        })}
      </Table>
    </>
  );
}

function InfoForm({ client, onSaved }: { client: ClientRow; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: client.name,
    email: client.email,
    contact_name: client.contact_name ?? '',
    phone: client.phone ?? '',
    notes: client.notes ?? '',
    billing_mode: client.billing_mode,
    low_credit_threshold: String(client.low_credit_threshold),
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const set = (field: string, value: string) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await operatorApi.patch(`/clients/${client.id}`, {
        name: form.name,
        email: form.email,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        notes: form.notes || null,
        billing_mode: form.billing_mode,
        low_credit_threshold: Number(form.low_credit_threshold),
      });
      setMessage({ ok: true, text: 'Modifications enregistrées.' });
      onSaved();
    } catch (e) {
      setMessage({ ok: false, text: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <form onSubmit={submit} className="grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Nom"><input className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} required minLength={2} /></Field>
        <Field label="Adresse e-mail"><input className={inputClass} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required /></Field>
        <Field label="Contact"><input className={inputClass} value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} /></Field>
        <Field label="Téléphone"><input className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
        <Field label="Mode de facturation" hint="Passer en accès libre désactive le contrôle du solde.">
          <select className={inputClass} value={form.billing_mode} onChange={(e) => set('billing_mode', e.target.value)}>
            <option value="prepaid">Lots prépayés</option>
            <option value="free">Accès libre</option>
          </select>
        </Field>
        <Field label="Seuil « à recharger »" hint="Le client apparaît dans les alertes sous ce nombre de signatures.">
          <input className={inputClass} type="number" min={0} value={form.low_credit_threshold} onChange={(e) => set('low_credit_threshold', e.target.value)} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Notes internes">
            <textarea className={inputClass} rows={4} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </Field>
        </div>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Button type="submit" loading={saving}>Enregistrer</Button>
          {message && <span className={`text-sm ${message.ok ? 'text-emerald-600' : 'text-red-600'}`}>{message.text}</span>}
        </div>
      </form>
    </Card>
  );
}

function CreditModal({ client, onClose, onDone }: { client: ClientRow; onClose: () => void; onDone: () => void }) {
  const settings = useFetch(async () => (await operatorApi.get<{ tariff: { tiers: TariffTier[] } }>('/settings')).data);
  const [kind, setKind] = useState<'purchase' | 'grant' | 'adjustment'>('purchase');
  const [credits, setCredits] = useState('50');
  const [price, setPrice] = useState('');
  const [priceEdited, setPriceEdited] = useState(false);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quantity = Number(credits) || 0;
  const suggested = settings.data ? priceForQuantity(settings.data.tariff.tiers, quantity) : 0;

  useEffect(() => {
    if (!priceEdited) setPrice((suggested / 100).toFixed(2));
  }, [suggested, priceEdited]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await operatorApi.post(`/clients/${client.id}/credits`, {
        kind,
        credits: quantity,
        amount_cents: kind === 'purchase' ? Math.round(Number(price.replace(',', '.')) * 100) : undefined,
        reference: reference || undefined,
        note: note || undefined,
      });
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Créditer ${client.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Opération">
          <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="purchase">Vente d&apos;un lot (encaissée)</option>
            <option value="grant">Signatures offertes</option>
            <option value="adjustment">Correction (positive ou négative)</option>
          </select>
        </Field>
        <Field label="Nombre de signatures" hint={kind === 'adjustment' ? 'Négatif pour retirer des signatures.' : undefined}>
          <input className={inputClass} type="number" step={1} value={credits} onChange={(e) => setCredits(e.target.value)} required autoFocus />
        </Field>
        {kind === 'purchase' && (
          <Field label="Prix encaissé (€)" hint={`Tarif au volume : ${eur(suggested)}${quantity > 0 ? ` soit ${eur(suggested / quantity)} par signature` : ''}. Modifiable pour une remise.`}>
            <input
              className={inputClass}
              inputMode="decimal"
              value={price}
              onChange={(e) => {
                setPrice(e.target.value);
                setPriceEdited(true);
              }}
              required
            />
          </Field>
        )}
        <Field label="Référence (facture, virement)">
          <input className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="FAC-2026-001" />
        </Field>
        <Field label="Note interne">
          <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={saving} disabled={quantity === 0}>Enregistrer</Button>
        </div>
      </form>
    </Modal>
  );
}

function KeyModal({ clientId, onClose, onDone }: { clientId: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('Production');
  const [readOnly, setReadOnly] = useState(false);
  const [days, setDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ api_key: string; prefix: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const response = await operatorApi.post(`/clients/${clientId}/keys`, { name, read_only: readOnly, expires_in_days: days ? Number(days) : undefined });
      setCreated(response.data);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <Modal title="Clé créée" onClose={onClose}>
        <div className="space-y-4">
          <SecretBox label={`Clé API ${created.prefix}…`} value={created.api_key} />
          <div className="flex justify-end"><Button onClick={onClose}>Fermer</Button></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Nouvelle clé API" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nom de la clé" hint="Par exemple Production ou Préproduction."><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required /></Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
          Lecture seule (consulter, sans créer ni envoyer)
        </label>
        <Field label="Expiration (jours, facultatif)"><input className={inputClass} type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} placeholder="Sans expiration" /></Field>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={saving}>Créer la clé</Button>
        </div>
      </form>
    </Modal>
  );
}
